"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { CircleCheck, FileText, UserCheck, Calendar } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EligibilityApplicationProcess() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const eligibilityCriteria = [{
    number: 1,
    title: "Academic Standing",
    description: "Must be enrolled in or accepted to a recognized secondary school or university in Uganda."
  }, {
    number: 2,
    title: "Financial Need",
    description: "Family income must fall below the established threshold, verified through documentation."
  }, {
    number: 3,
    title: "Commitment to Community",
    description: "Demonstrated interest in giving back through volunteer work or community engagement."
  }, {
    number: 4,
    title: "Academic Performance",
    description: "Maintain a minimum GPA or equivalent academic standard as outlined in program guidelines."
  }];
  const applicationSteps = [{
    icon: FileText,
    title: "Complete Application Form",
    description: "Fill out the online application with personal, academic, and financial information.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1518961/img/application_form_icon.png"
  }, {
    icon: UserCheck,
    title: "Submit Supporting Documents",
    description: "Provide academic transcripts, proof of enrollment, and financial verification documents.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1518961/img/supporting_documents_icon.png"
  }, {
    icon: Calendar,
    title: "Attend Interview (If Selected)",
    description: "Shortlisted candidates will be invited for a brief interview to discuss goals and needs.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1518961/img/interview_process_icon.png"
  }, {
    icon: CircleCheck,
    title: "Receive Decision Notification",
    description: "Applicants will be notified of their scholarship status via email within 4-6 weeks.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1518961/img/scholarship_decision_icon.png"
  }];
  return <section ref={sectionRef} className="py-16 md:py-24 px-4 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Eligibility & Application Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Learn who qualifies for JEEN scholarships and follow our step-by-step application guide to begin your educational journey.
          </p>
        </div>

        {}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {}
          <div className={`bg-muted-light rounded-2xl p-6 md:p-8 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`}>
            <h3 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-6">
              Eligibility Criteria
            </h3>
            <div className="space-y-4">
              {eligibilityCriteria.map((item, i) => <div key={i} data-index={i} className={`flex gap-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`} style={{
              transitionDelay: `${200 + i * 50}ms`
            }}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-foreground-primary flex items-center justify-center font-bold text-lg">
                    {item.number}
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className={`bg-dark-background rounded-[2rem] p-6 md:p-8 relative overflow-hidden transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`} style={{
          clipPath: "ellipse(100% 100% at 50% 50%)"
        }}>
            {}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-display font-semibold text-foreground-dark mb-8">
                Application Steps
              </h3>
              <div className="space-y-6">
                {applicationSteps.map((step, i) => {
                const IconComponent = step.icon;
                return <div key={i} data-index={i} className={`group flex gap-4 items-start transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] hover:scale-105 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`} style={{
                  transitionDelay: `${300 + i * 50}ms`
                }}>
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-foreground-accent transition-all duration-[var(--duration-fast)]">
                        <IconComponent className="size-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-dark mb-1">{step.title}</h4>
                        <p className="text-sm text-foreground-dark/80">{step.description}</p>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className={`mt-12 text-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-[400ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`}>
          <Link to="#apply">
            <Button size="lg" className="btn-primary shadow-medium hover:shadow-strong">
              Start Your Application
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}